import { Dispatch, SetStateAction, FC, useEffect, ReactNode, ComponentProps } from 'react';

import { DATE_FORMAT_ISO, parse } from '@eneco/common/date';
import RichText from '@eneco/components/RichText/RichText';
import { TrackedDialog } from '@eneco/components/TrackedDialog/TrackedDialog';
import { productUnits } from '@eneco/enumerations/productUnits';
import { useFormatter } from '@eneco/i18n';
import {
  Expandable,
  Box,
  Stack,
  NotificationBox,
  TabSwitch,
  Divider,
  TextLink,
  Text,
  Bleed,
  Popover,
} from '@eneco/sparky';
import { InfoIcon } from '@eneco/sparky/icons';
import { DC_Domain_Models_Products_ProductType } from '@monorepo-types/dc';
import { useForm, Controller, useWatch } from 'react-hook-form';

import type { OfferSectionTextualData } from './OfferSection.types';
import { FlowConsumerContextEnergy } from '../../../context';
import {
  checkOfferIncludesDynamicPricingProduct,
  getProductTitleFromProductType,
  checkOffersIncludeVariablePricingProduct,
} from '../../helpers';
import { useProductItemContent } from '../../hooks';
import { DetailsType, Offer, Product, CostDetails, OfferType } from '../../types';
import { typeTheme } from '../PriceDetails/PriceDetails';

const ExpandableOfferSection: FC<{
  offer: Offer;
  textualData?: OfferSectionTextualData;
  flowContext?: FlowConsumerContextEnergy;
  isOpen: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  setCostPeriod: Dispatch<SetStateAction<string>>;
  textualDataKey: string;
  showProductName?: boolean;
  showLegalText?: boolean;
  showEndOfContractDialog?: boolean;
  children: ReactNode;
  /** Vertical padding of the expandable trigger ("Bekijk prijsopbouw") row. Defaults to '6'. */
  triggerPaddingY?: ComponentProps<typeof Box>['paddingY'];
  showEnergyLabel?: boolean;
  /**
   * When this section sits inside a radio-card group, only the selected card's
   * trigger should be a tab stop (WCAG 2.4.3 / APG radio group). Defaults to true.
   */
  isSelected?: boolean;
  /** Select this offer when the trigger is used while the card is not selected. */
  onSelect?: () => void;
}> = ({
  offer,
  textualData,
  flowContext,
  isOpen,
  setOpen,
  setCostPeriod,
  textualDataKey,
  showProductName,
  showLegalText,
  showEndOfContractDialog,
  children,
  triggerPaddingY = '6',
  showEnergyLabel,
  isSelected = true,
  onSelect,
}) => {
  const formMethods = useForm<{ costPeriod: string }>({
    mode: 'onBlur',
    defaultValues: {
      costPeriod: 'costsMonthly',
    },
  });
  const { control } = formMethods;
  const { costPeriod = 'costsMonthly' } = useWatch({ control });

  useEffect(() => {
    setCostPeriod(costPeriod);
  }, [costPeriod, setCostPeriod]);

  return (
    <Expandable
      isOpen={isSelected && isOpen}
      setOpen={nextOpen => {
        if (!isSelected) onSelect?.();
        setOpen(nextOpen);
      }}>
      <Box paddingRight="6">
        <Expandable.Trigger
          isTransparent
          aria-labelledby={`${offer.type}-tariff-details`}
          tabIndex={isSelected ? undefined : -1}>
          <Box id={`${offer.type}-tariff-details`} paddingLeft="6" paddingY={triggerPaddingY}>
            {textualData?.productData?.tariffsTriggerText?.value ?? 'Bekijk tarieven'}
          </Box>
        </Expandable.Trigger>
      </Box>

      <Expandable.Content>
        <OfferSection
          offer={offer}
          textualData={textualData}
          flowContext={flowContext}
          setCostPeriod={setCostPeriod}
          textualDataKey={textualDataKey}
          showProductName={showProductName}
          showLegalText={showLegalText}
          showEndOfContractDialog={showEndOfContractDialog}
          showEnergyLabel={showEnergyLabel}>
          {children}
        </OfferSection>
      </Expandable.Content>
    </Expandable>
  );
};

export default ExpandableOfferSection;

export const OfferSection: FC<{
  offer: Offer;
  textualData?: OfferSectionTextualData;
  flowContext?: FlowConsumerContextEnergy;
  setCostPeriod: Dispatch<SetStateAction<string>>;
  textualDataKey: string;
  showProductName?: boolean;
  showLegalText?: boolean;
  contractStartDateLabel?: string;
  showPriceBreakdown?: boolean;
  contractEndDateLabel?: string;
  showEndOfContractDialog?: boolean;
  showEnergyLabel?: boolean;
  children?: ReactNode;
}> = ({
  contractStartDateLabel,
  contractEndDateLabel,
  offer,
  textualData,
  flowContext,
  setCostPeriod,
  textualDataKey,
  showProductName = true,
  showLegalText = true,
  showPriceBreakdown = true,
  showEndOfContractDialog = true,
  showEnergyLabel = true,
  children,
}) => {
  const { discountType } = offer || {};
  const { currency, date } = useFormatter();
  const formMethods = useForm<{ costPeriod: string }>({
    mode: 'onBlur',
    defaultValues: {
      costPeriod: 'costsMonthly',
    },
  });
  const { control } = formMethods;
  const { costPeriod = 'costsMonthly' } = useWatch({ control });

  type CostPeriod = 'costsMonthly' | 'costsYearly';

  const periodLabel: Record<CostPeriod, string> = {
    costsMonthly: 'per maand',
    costsYearly: 'per jaar',
  };

  useEffect(() => {
    setCostPeriod(costPeriod);
  }, [costPeriod, setCostPeriod]);

  const { getProductDetails } = useProductItemContent(textualData?.productData?.productDataList);

  const label = periodLabel[(costPeriod ?? 'costsMonthly') as 'costsMonthly' | 'costsYearly'];
  const getCostPerPeriod = (data: Offer | Product) =>
    costPeriod === 'costsMonthly' ? data.costsMonthly : data.costsYearly;

  const productDetails = getProductDetails(textualDataKey);

  const tariffDescriptionText = productDetails?.tariffDescription;
  const tariffSplitExplanationText = productDetails?.tariffSplitExplanation;

  const shouldShowVariablePricingNotification =
    discountType?.includes(OfferType.Variable) &&
    !!textualData?.productData?.variableProductNotification?.value?.content;
  const contractEndDate = offer?.contractEndDate;
  const shouldShowEndDate = contractEndDate && contractEndDateLabel && !checkOffersIncludeVariablePricingProduct(offer);

  const getEnergyLabel = (productType: DC_Domain_Models_Products_ProductType, costDetails: CostDetails) => {
    const electricityHigh = currency.euroHighPrecision(
      costDetails?.electricityCostDetails?.tariff?.price?.vatIncluded ?? 0,
    );
    const electricityLow = currency.euroHighPrecision(
      costDetails?.electricityCostDetails?.lowtariff?.price?.vatIncluded ?? 0,
    );
    const electricityPurchaseFee = `${currency.euroHighPrecision(
      costDetails?.electricityCostDetails?.purchaseFee?.price?.vatIncluded ?? 0,
    )} / ${productUnits.electricity}`;
    const gasPurchaseFee = `${currency.euroHighPrecision(
      costDetails?.gasCostDetails?.purchaseFee?.price?.vatIncluded ?? 0,
    )} / ${productUnits.gas}`;

    const firstTariffCostDetail = costDetails?.tariffCostDetails;
    const gasPrice = currency.euroHighPrecision(firstTariffCostDetail?.vatIncluded ?? 0);

    const normal = `${electricityHigh} / ${productUnits.electricity}`;
    const dal = `${electricityLow} / ${productUnits.electricity}`;
    const gas = `${gasPrice} / ${productUnits.gas}`;

    if (productType === 'electricity') {
      if (flowContext?.hasDoubleMeter) {
        if (
          checkOfferIncludesDynamicPricingProduct(offer) &&
          costDetails?.electricityCostDetails?.purchaseFee?.price?.vatIncluded
        ) {
          const dynamicProductLabel =
            `<br />geschatte marktwaarde ${electricityHigh} ` + `<br />inkoopvergoeding ${electricityPurchaseFee}`;

          return productDetails?.electricityDeliveryRateDescription?.value
            ?.toString()
            ?.replace('{value}', dynamicProductLabel);
        }

        const doubleMeterLabel =
          `<br />${productDetails?.unitPriceLabels?.electricityHighLabel || textualData?.unitPriceLabels?.electricityHighLabel?.value} ${normal} ` +
          `<br />${productDetails?.unitPriceLabels?.electricityLowLabel || textualData?.unitPriceLabels?.electricityLowLabel?.value} ${dal}`;

        return productDetails?.electricityDeliveryRateDescription?.value
          ?.toString()
          ?.replace('{value}', doubleMeterLabel);
      }
      const singleMeterLabel = `<br />${productDetails?.unitPriceLabels?.electricityLabel || textualData?.unitPriceLabels?.electricityLabel?.value} ${normal}`;

      return productDetails?.electricityDeliveryRateDescription?.value
        ?.toString()
        ?.replace('{value}', singleMeterLabel);
    }

    if (productType === 'gas') {
      if (costDetails?.gasCostDetails?.purchaseFee?.price?.vatIncluded) {
        const dynamicProductLabel =
          `<br />geschatte marktwaarde ${gasPrice} ` + `<br />inkoopvergoeding ${gasPurchaseFee}`;

        return productDetails?.gasDeliveryRateDescription?.value?.toString()?.replace('{value}', dynamicProductLabel);
      }
      const gasLabel = `<br />${productDetails?.unitPriceLabels?.gasLabel || textualData?.unitPriceLabels?.gasLabel?.value} ${gas}`;

      return productDetails?.gasDeliveryRateDescription?.value?.toString()?.replace('{value}', gasLabel);
    }
  };

  const getEnergyDisclosurePopoverData = (productType: DC_Domain_Models_Products_ProductType) => {
    if (productType === 'electricity') {
      return textualData?.productData?.electricityDisclosureDescription;
    }

    if (productType === 'gas') {
      return textualData?.productData?.gasDisclosureDescription;
    }

    if (productType === 'warmth') {
      return textualData?.productData?.warmthDisclosureDescription;
    }

    return null;
  };

  return (
    <Box padding="6" paddingTop="0">
      <Stack gap="6">
        {shouldShowVariablePricingNotification && (
          <NotificationBox
            isAlert={false}
            variant={textualData?.productData?.variableProductNotification?.value?.variant}
            title={textualData?.productData?.variableProductNotification?.value?.title}
            text={<RichText html={textualData?.productData?.variableProductNotification?.value?.content} />}
          />
        )}

        <Controller
          control={control}
          name="costPeriod"
          render={({ field: { onChange, value, name } }) => (
            <TabSwitch
              name={name}
              value={value}
              onValueChange={val => {
                onChange(val);
              }}>
              <TabSwitch.Item key="costsMonthly" value="costsMonthly">
                Prijs per maand
              </TabSwitch.Item>

              <TabSwitch.Item key="costsYearly" value="costsYearly">
                Prijs per jaar
              </TabSwitch.Item>
            </TabSwitch>
          )}
        />
        <Stack gap="4">
          {offer.products.map(product => {
            const energyDisclosureData = getEnergyDisclosurePopoverData(product.type);

            return (
              <>
                <Stack key={product.code} direction="row" alignY="start" gap="2">
                  <Stack.Item grow>
                    <Stack direction="row" gap="2" alignY="start">
                      {typeTheme[product.type as DetailsType]?.icon}
                      <Stack direction="column" alignY="center">
                        <Stack direction="row" gap={2}>
                          <Text weight="bold">
                            {getProductTitleFromProductType(product.type, textualData?.productTypeLabels)}
                          </Text>
                          {energyDisclosureData?.value && (
                            <Popover
                              side="bottom"
                              size="small"
                              title={energyDisclosureData?.value}
                              trigger={
                                <TextLink
                                  leftIcon={<InfoIcon size="small" verticalAlign="text-top" color={'currentColor'} />}>
                                  {''}
                                </TextLink>
                              }>
                              <Text weight="regular">
                                <RichText html={energyDisclosureData?.value} />
                              </Text>
                            </Popover>
                          )}
                        </Stack>
                        {showProductName && <Text weight="regular">{product.description}</Text>}
                      </Stack>
                    </Stack>
                  </Stack.Item>
                  <Stack.Item shrink={false}>
                    <Stack direction="column" alignX="end" gap="0">
                      <Text weight="bold">
                        {currency.euroHighPrecision(getCostPerPeriod(product)?.vatIncluded ?? 0)}
                      </Text>
                      <Text size="BodyS">{label}</Text>
                    </Stack>
                  </Stack.Item>
                </Stack>
                <Divider />
                {showEnergyLabel && getEnergyLabel(product.type, product.costDetails) && (
                  <Bleed top={2}>
                    <Box paddingLeft={8} paddingRight={4}>
                      <Text size="BodyS" color="textLowEmphasis">
                        <RichText html={getEnergyLabel(product.type, product.costDetails)} />
                      </Text>
                    </Box>
                  </Bleed>
                )}
              </>
            );
          })}
          <Stack direction="row" alignY="start" gap="2">
            <Stack.Item grow={true}>
              <Stack direction="row" gap="2">
                <Text weight="bold">Totaal</Text>
              </Stack>
            </Stack.Item>

            <Stack.Item shrink={false}>
              <Stack direction="column" alignY="end" gap="2">
                <Stack direction="column" alignY="end" alignX="end">
                  <Stack.Item grow={true}>
                    <Text weight="bold">{currency.euroHighPrecision(getCostPerPeriod(offer)?.vatIncluded ?? 0)}</Text>
                  </Stack.Item>
                  <Stack.Item grow={true}>
                    <Text size="BodyS">{label}</Text>
                  </Stack.Item>
                </Stack>
              </Stack>
            </Stack.Item>
          </Stack>
          {contractStartDateLabel && (
            <>
              <Divider />
              <Stack direction="column" gap="1">
                <Stack direction="row" gap="1" alignX="justify">
                  <Text>{contractStartDateLabel}</Text>
                  <Text>{offer?.contractStartDate && date.short(parse(offer.contractStartDate, DATE_FORMAT_ISO))}</Text>
                </Stack>

                {shouldShowEndDate && (
                  <Stack direction="row" gap="1" alignX="justify">
                    <Text>{contractEndDateLabel}</Text>
                    <Text>{date.short(parse(contractEndDate, DATE_FORMAT_ISO))}</Text>
                  </Stack>
                )}
              </Stack>
            </>
          )}
        </Stack>
        {showEndOfContractDialog && productDetails?.endOfContractInformationDialog?.value?.triggerText && (
          <Stack>
            <Text size="BodyS">
              <TrackedDialog
                title={productDetails?.endOfContractInformationDialog?.value?.title}
                trigger={
                  <TextLink emphasis="low">
                    {productDetails?.endOfContractInformationDialog?.value?.triggerText}
                  </TextLink>
                }>
                <RichText html={productDetails?.endOfContractInformationDialog?.value?.content} />
              </TrackedDialog>
            </Text>
          </Stack>
        )}

        <Stack gap="3">
          {showPriceBreakdown && (
            <Text size="BodyS">
              <TrackedDialog
                title="Bekijk de prijsopbouw"
                trigger={<TextLink emphasis="high">Bekijk de prijsopbouw</TextLink>}>
                {children}
              </TrackedDialog>
            </Text>
          )}
          {showLegalText && (
            <>
              {offer.productFutureRate && (
                <Box paddingTop={{ initial: '3', md: '0' }}>
                  <Text size="BodyXS" color="textLowEmphasis">
                    {tariffSplitExplanationText}
                  </Text>
                </Box>
              )}

              {tariffDescriptionText && (
                <Box paddingTop={{ initial: '3', md: '0' }}>
                  <Text size="BodyXS" color="textLowEmphasis">
                    {tariffDescriptionText}
                  </Text>
                </Box>
              )}
            </>
          )}
        </Stack>
      </Stack>
    </Box>
  );
};
